import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, ChevronRight, Users, BookOpen, MessageSquare, FileText, Send } from 'lucide-react';
import { Layout } from '../../components/layout/Layout';
import { PageHeader } from '../../components/layout/PageHeader';
import { Card, CardContent } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Spinner } from '../../components/ui/Spinner';
import {
  getTeacherMyClasses,
  getTeacherMyStudents,
  TeacherClass,
  TeacherStudentEntry,
} from '../../modules/roster/api/teacher.client';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '../../components/ui/Table';
import { Modal } from '../../components/ui/Modal';
import { Button } from '../../components/ui/Button';
import { showErrorToast, showSuccessToast } from '../../utils/errorHandler';
import { ROUTES } from '../../utils/constants';

type ViewMode = 'classes' | 'students';

export const MySectionsPage: React.FC = () => {
  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState<ViewMode>('classes');
  const [chatStudent, setChatStudent] = useState<{ id: string; name: string } | null>(null);
  const [chatMessage, setChatMessage] = useState('');
  const [classes, setClasses] = useState<TeacherClass[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [gradeFilter, setGradeFilter] = useState<string>('all');
  const [yearFilter, setYearFilter] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [students, setStudents] = useState<TeacherStudentEntry[]>([]);
  const [studentsLoading, setStudentsLoading] = useState(false);
  const [expandedClassIds, setExpandedClassIds] = useState<Set<string>>(new Set());

  const toggleExpand = useCallback((classId: string) => {
    setExpandedClassIds((prev) => {
      const next = new Set(prev);
      if (next.has(classId)) next.delete(classId);
      else next.add(classId);
      return next;
    });
  }, []);

  useEffect(() => {
    const loadClasses = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await getTeacherMyClasses();
        setClasses(data);
      } catch (err) {
        console.error(err);
        setError('Unable to load classes.');
        showErrorToast('Unable to load classes. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    loadClasses();
  }, []);

  useEffect(() => {
    if (viewMode === 'classes' && gradeFilter === 'all') {
      setStudents([]);
      setExpandedClassIds(new Set());
      return;
    }
    const loadStudents = async () => {
      setStudentsLoading(true);
      try {
        const params: { grade?: number; academic_year_key?: string } = {};
        if (gradeFilter !== 'all') params.grade = parseInt(gradeFilter, 10);
        if (yearFilter !== 'all') params.academic_year_key = yearFilter;
        const data = await getTeacherMyStudents(params);
        setStudents(data);
      } catch (err) {
        console.error(err);
        setStudents([]);
        showErrorToast('Unable to load students. Please try again.');
      } finally {
        setStudentsLoading(false);
      }
    };
    loadStudents();
  }, [viewMode, gradeFilter, yearFilter]);

  const gradeOptions = useMemo(() => {
    const grades = new Set<string>();
    classes.forEach((cls) => {
      const g = cls.grade;
      if (g != null && String(g).trim() !== '') grades.add(String(g).trim());
    });
    const sorted = Array.from(grades).sort((a, b) => parseInt(a, 10) - parseInt(b, 10));
    return [
      { label: 'All Grades', value: 'all' },
      ...sorted.map((g) => ({ label: `Grade ${g}`, value: g })),
    ];
  }, [classes]);

  const academicYears = useMemo(() => {
    const years = new Set<string>();
    classes.forEach((cls) => {
      const year = cls.academic_year_key;
      if (year) years.add(String(year));
    });
    return Array.from(years).sort();
  }, [classes]);

  useEffect(() => {
    if (gradeFilter !== 'all') {
      const validGrades = gradeOptions.filter((o) => o.value !== 'all').map((o) => o.value);
      if (!validGrades.includes(gradeFilter)) {
        setGradeFilter('all');
      }
    }
  }, [gradeOptions, gradeFilter]);

  const filteredClasses = useMemo(() => {
    let filtered = classes;
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      filtered = filtered.filter((cls) =>
        (cls.name ?? '').toLowerCase().includes(q)
      );
    }
    if (gradeFilter !== 'all') {
      filtered = filtered.filter(
        (cls) => String(cls.grade ?? '').trim() === gradeFilter
      );
    }
    if (yearFilter !== 'all') {
      filtered = filtered.filter(
        (cls) => String(cls.academic_year_key ?? '').trim() === yearFilter
      );
    }
    return filtered;
  }, [classes, searchQuery, gradeFilter, yearFilter]);

  const studentsByClassId = useMemo(() => {
    const map = new Map<string, TeacherStudentEntry[]>();
    students.forEach((entry) => {
      const cid = entry.class?.id;
      if (cid) {
        const list = map.get(cid) ?? [];
        list.push(entry);
        map.set(cid, list);
      }
    });
    return map;
  }, [students]);

  const filteredStudents = useMemo(() => {
    if (!searchQuery.trim()) return students;
    const q = searchQuery.toLowerCase().trim();
    return students.filter((entry) => {
      const s = entry.student;
      const name =
        s.display_name ||
        [s.first_name, s.last_name].filter(Boolean).join(' ') ||
        '';
      const admissionNo = s.admission_no || '';
      return (
        name.toLowerCase().includes(q) ||
        admissionNo.toLowerCase().includes(q)
      );
    });
  }, [students, searchQuery]);

  const hasActiveFilters = searchQuery.trim() || gradeFilter !== 'all' || yearFilter !== 'all';
  const isEmpty = filteredClasses.length === 0;
  const isEmptyStudents = filteredStudents.length === 0;

  return (
    <Layout>
      <PageHeader title="My Classes" subtitle="View your assigned classes" />
      <div className="max-w-7xl mx-auto">
        <Card>
          <CardContent padding="md">
            {loading ? (
              <div className="flex justify-center py-10">
                <Spinner size="lg" />
              </div>
            ) : error ? (
              <div className="text-sm text-error-600 dark:text-error-400 py-6 text-center">
                {error}
              </div>
            ) : (
              <>
                <div className="flex flex-wrap items-center gap-4 mb-4">
                  <div
                    className="inline-flex rounded-lg p-1 bg-app-surface-muted dark:bg-gray-800"
                    role="tablist"
                  >
                    <button
                      type="button"
                      role="tab"
                      aria-selected={viewMode === 'classes'}
                      onClick={() => setViewMode('classes')}
                      className={`inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                        viewMode === 'classes'
                          ? 'bg-app-surface dark:bg-gray-700 text-primary-600 dark:text-primary-400 shadow-sm'
                          : 'text-app-text-muted dark:text-gray-400 hover:text-app-text dark:hover:text-white'
                      }`}
                    >
                      <BookOpen className="h-4 w-4" />
                      Classes
                    </button>
                    <button
                      type="button"
                      role="tab"
                      aria-selected={viewMode === 'students'}
                      onClick={() => setViewMode('students')}
                      className={`inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                        viewMode === 'students'
                          ? 'bg-app-surface dark:bg-gray-700 text-primary-600 dark:text-primary-400 shadow-sm'
                          : 'text-app-text-muted dark:text-gray-400 hover:text-app-text dark:hover:text-white'
                      }`}
                    >
                      <Users className="h-4 w-4" />
                      Students
                    </button>
                  </div>
                  <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-3 min-w-0">
                    <Input
                      placeholder={
                        viewMode === 'classes'
                          ? 'Search by class name...'
                          : 'Search by student name or admission no...'
                      }
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                    />
                    <Select
                    options={gradeOptions}
                    value={gradeFilter}
                    onChange={(e) => setGradeFilter(e.target.value)}
                  />
                  <Select
                    options={[
                      { label: 'All Years', value: 'all' },
                      ...academicYears.map((year) => ({ label: year, value: year })),
                    ]}
                    value={yearFilter}
                    onChange={(e) => setYearFilter(e.target.value)}
                  />
                  </div>
                </div>

                {viewMode === 'classes' && (isEmpty ? (
                  <div className="text-sm text-gray-600 dark:text-gray-400 text-center py-12">
                    {hasActiveFilters ? 'No classes match your filters.' : 'No classes assigned yet.'}
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-10">{'\u00A0'}</TableHead>
                        <TableHead>Class Name</TableHead>
                        <TableHead>Grade</TableHead>
                        <TableHead>Academic Year</TableHead>
                        <TableHead>Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredClasses.map((cls, index) => {
                        const classKey = cls.id ?? `${cls.name}-${cls.grade}-${cls.academic_year_key}-${index}`;
                        const isExpanded = expandedClassIds.has(classKey);
                        const canExpand = gradeFilter !== 'all';
                        const classStudents = (cls.id ? studentsByClassId.get(cls.id) : undefined) ?? [];
                        const isLoadingStudents = canExpand && studentsLoading;

                        return (
                          <React.Fragment key={classKey}>
                            <TableRow>
                              <TableCell className="w-10">
                                {canExpand ? (
                                  <button
                                    type="button"
                                    onClick={() => toggleExpand(classKey)}
                                    className="p-1 rounded hover:bg-app-surface-muted dark:hover:bg-gray-800 transition-colors"
                                    aria-label={isExpanded ? 'Collapse' : 'Expand'}
                                  >
                                    {isExpanded ? (
                                      <ChevronDown className="h-5 w-5 text-app-text-muted" />
                                    ) : (
                                      <ChevronRight className="h-5 w-5 text-app-text-muted" />
                                    )}
                                  </button>
                                ) : null}
                              </TableCell>
                              <TableCell className="font-medium text-gray-900 dark:text-white">
                                {cls.name}
                              </TableCell>
                              <TableCell className="text-gray-700 dark:text-gray-300">
                                {cls.grade != null && cls.grade !== '' ? String(cls.grade) : '-'}
                              </TableCell>
                              <TableCell className="text-gray-700 dark:text-gray-300">
                                {cls.academic_year_key ?? '-'}
                              </TableCell>
                              <TableCell>
                                <Badge
                                  variant={
                                    cls.status === 'active'
                                      ? 'success'
                                      : cls.status === 'inactive'
                                        ? 'gray'
                                        : 'gray'
                                  }
                                >
                                  {cls.status ?? '-'}
                                </Badge>
                              </TableCell>
                            </TableRow>
                            {canExpand && isExpanded && (
                              <>
                                {isLoadingStudents ? (
                                  <TableRow className="bg-app-surface-muted/50 dark:bg-gray-800/50">
                                    <TableCell colSpan={5} className="pl-12 py-4">
                                      <div className="flex items-center gap-2 text-sm text-app-text-muted">
                                        <Spinner size="sm" />
                                        Loading students...
                                      </div>
                                    </TableCell>
                                  </TableRow>
                                ) : classStudents.length === 0 ? (
                                  <TableRow className="bg-app-surface-muted/50 dark:bg-gray-800/50">
                                    <TableCell colSpan={5} className="pl-12 py-4 text-sm text-app-text-muted">
                                      No students in this class.
                                    </TableCell>
                                  </TableRow>
                                ) : (
                                  classStudents.map((entry) => {
                                    const student = entry.student;
                                    return (
                                      <TableRow
                                        key={entry.enrollment.id}
                                        className="bg-app-surface-muted/50 dark:bg-gray-800/50"
                                      >
                                        <TableCell>{''}</TableCell>
                                        <TableCell className="pl-12 font-medium text-gray-900 dark:text-white">
                                          {student.display_name ||
                                            [student.first_name, student.last_name].filter(Boolean).join(' ') ||
                                            student.id}
                                        </TableCell>
                                        <TableCell className="text-gray-700 dark:text-gray-300">
                                          {student.admission_no || '-'}
                                        </TableCell>
                                        <TableCell className="text-gray-700 dark:text-gray-300">
                                          {entry.section?.name ?? '-'}
                                        </TableCell>
                                        <TableCell>
                                          <Badge variant={student.status === 'active' ? 'success' : 'gray'}>
                                            {student.status || '-'}
                                          </Badge>
                                        </TableCell>
                                      </TableRow>
                                    );
                                  })
                                )}
                              </>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </TableBody>
                  </Table>
                ))}

                {viewMode === 'students' && (
                  studentsLoading ? (
                    <div className="flex justify-center py-10">
                      <Spinner size="lg" />
                    </div>
                  ) : isEmptyStudents ? (
                    <div className="text-sm text-gray-600 dark:text-gray-400 text-center py-12">
                      {hasActiveFilters
                        ? 'No students match your filters.'
                        : 'No students assigned yet.'}
                    </div>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Name</TableHead>
                          <TableHead>Admission No</TableHead>
                          <TableHead>Class</TableHead>
                          <TableHead>Section</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead className="w-24">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredStudents.map((entry) => {
                          const student = entry.student;
                          const studentName =
                            student.display_name ||
                            [student.first_name, student.last_name].filter(Boolean).join(' ') ||
                            student.id;
                          return (
                            <TableRow key={entry.enrollment.id}>
                              <TableCell className="font-medium text-gray-900 dark:text-white">
                                {studentName}
                              </TableCell>
                              <TableCell className="text-gray-700 dark:text-gray-300">
                                {student.admission_no || '-'}
                              </TableCell>
                              <TableCell className="text-gray-700 dark:text-gray-300">
                                {entry.class?.name ?? '-'}
                              </TableCell>
                              <TableCell className="text-gray-700 dark:text-gray-300">
                                {entry.section?.name ?? '-'}
                              </TableCell>
                              <TableCell>
                                <Badge variant={student.status === 'active' ? 'success' : 'gray'}>
                                  {student.status || '-'}
                                </Badge>
                              </TableCell>
                              <TableCell>
                                <div className="flex items-center gap-1">
                                  <button
                                    type="button"
                                    onClick={() =>
                                      setChatStudent({ id: student.id, name: studentName })
                                    }
                                    className="p-2 rounded hover:bg-app-surface-muted dark:hover:bg-gray-800 transition-colors text-app-text-muted hover:text-primary-600 dark:hover:text-primary-400"
                                    title="Chat"
                                    aria-label="Chat with student"
                                  >
                                    <MessageSquare className="h-5 w-5" />
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() =>
                                      navigate(`${ROUTES.TEACHER_ASSIGNMENTS}?studentId=${student.id}`)
                                    }
                                    className="p-2 rounded hover:bg-app-surface-muted dark:hover:bg-gray-800 transition-colors text-app-text-muted hover:text-primary-600 dark:hover:text-primary-400"
                                    title="Assignments"
                                    aria-label="View assignments"
                                  >
                                    <FileText className="h-5 w-5" />
                                  </button>
                                </div>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  )
                )}
              </>
            )}
          </CardContent>
        </Card>

        <Modal
          isOpen={!!chatStudent}
          onClose={() => {
            setChatStudent(null);
            setChatMessage('');
          }}
          title={chatStudent ? `Chat with ${chatStudent.name}` : ''}
          size="lg"
          showCloseButton
        >
          {chatStudent && (
            <div className="flex flex-col h-[400px]">
              <div className="flex-1 overflow-y-auto py-4 px-2 min-h-[200px] bg-app-surface-muted/30 dark:bg-gray-900/30 rounded-lg mb-4">
                <div className="text-sm text-app-text-muted dark:text-gray-400 text-center py-8">
                  No messages yet. Start the conversation!
                </div>
              </div>
              <div className="flex gap-2 shrink-0">
                <div className="flex-1 min-w-0">
                  <Input
                    placeholder="Type a message..."
                    value={chatMessage}
                    onChange={(e) => setChatMessage(e.target.value)}
                  />
                </div>
                <Button
                  variant="primary"
                  onClick={() => {
                    showSuccessToast('Chat coming soon!');
                  }}
                  className="shrink-0 px-4"
                  aria-label="Send message"
                >
                  <Send className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </Modal>
      </div>
    </Layout>
  );
};